import React from "react";
import BaseGroup from "./BaseGroup.component";
import BaseInput from "./BaseInput.component";
import BaseText from "./BaseText.component";
import BaseSelect from "./BaseSelect.component";
import BaseSwitch from "./BaseSwitch.component";
import BaseCheckbox from "./BaseCheckbox.component";
import BaseButtonSelector from "./BaseButtonSelector.component";
import BaseBulletsSelector from "./BaseBulletsSelect.component";
import BaseGameSelector from "./BaseGameSelect.component";
import BaseDate from "./BaseDate.component";
import BaseTwoPanelSelector from "./BaseTwoPanelSelector.component";
import BaseFormSet from "./BaseFormSet.component";
import BaseNumber from "./BaseNumber.component";
import BaseFormSetWays from "./BaseFormSetWays.component";
import BaseNumbersArray from "./BaseNumbersArray.component";

import {
    InputFieldOptions,
    SelectFieldOptions,
    CheckboxFieldOptions,
    ButtonSelectorFieldOptions,
    TwoPanelSelectorOptions,
    FormSetOptions,
    FormSetWaysOptions,
    TextareaFieldOptions,
    SwitchFieldOptions,
    BulletsSelectOptions,
    DateFieldOptions,
    GameSelectorFieldOptions,
    NumberArrayFieldOptions,
    BetJackpotArrayFieldOptions,
    BetFreqPrizesArrayFieldOptions,
    InputAliasFieldOptions,
    InputSearchFieldOptions
} from "./FormGrid";
import BaseBetJackpotArray from "./BaseBetJackpotArray.component";
import BaseInputAlias from "./BaseAlias.component";
import BaseBetFreqPrizesArray from "./BaseBetFreqPrizesArray.component"
import InputSearch from "./InputSearch.component";

const validatorsMap = {
    number: () => "Must be an number;",
    integer: () => "Must be an integer;",
    minLength: minLen => `Min. ${minLen} character(s);`,
    maxLength: maxLen => `Max. ${maxLen} character(s);`,
    pattern: () => `Must match a specific format;`,
    maximum: maxValue => `Must be <= than ${maxValue};`,
    minimum: minValue => `Must be >= than ${minValue};`,
    exclusiveMinimum: excMin => `Must be > than ${excMin};`,
    info_message: message => `${message};`
};

const createMessages = validationSchema => {
    let messages = [];
    const convert =
        validationSchema.hasOwnProperty("convert") &&
        validationSchema.convert == true;
    Object.keys(validationSchema).map(key => {
        if (validatorsMap.hasOwnProperty(key)) {
            messages.push(
                convert
                    ? validatorsMap[key](validationSchema[key] / 100)
                    : validatorsMap[key](validationSchema[key])
            );
        }
    });
    return messages;
};

const renderToolTip = validationSchema => {
    const messages = !validationSchema ? [] : createMessages(validationSchema);
    return messages.length > 0 ? (
        <React.Fragment>
            <button>
                <i className="icon-question-circle-solid"></i>
            </button>
            <div className="messages">
                {messages.map((message, index) => {
                    return <li key={index}>{message}</li>;
                })}
            </div>
        </React.Fragment>
    ) : null;
};

export const inputContainer =
    ({
        id,
        placeholder,
        inputType,
        label,
        disabled,
        validationSchema,
        isConfig
    }: InputFieldOptions) =>
    props => {
        return (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseInput
                    placeholder={placeholder}
                    inputtype={inputType}
                    givenid={id}
                    disabled={disabled ? "disabled" : ""}
                    isConfig={isConfig}
                    {...props}
                />
            </div>
        );
    };

export const textareaContainer =
    ({
        id,
        placeholder,
        label,
        disabled,
        classNames,
        validationSchema,
        isConfig
    }: TextareaFieldOptions) =>
    props => {
        return (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseText
                    placeholder={placeholder}
                    givenid={id}
                    disabled={disabled}
                    className={classNames}
                    isConfig={isConfig}
                    {...props}
                />
            </div>
        );
    };

export const numberContainer =
    ({
        id,
        placeholder,
        inputType,
        label,
        disabled,
        decimal,
        validationSchema,
        isConfig
    }: InputFieldOptions) =>
    props => {
        return (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseNumber
                    placeholder={placeholder}
                    inputtype={"number"}
                    givenid={id}
                    decimal={decimal}
                    disabled={disabled ? "disabled" : ""}
                    isConfig={isConfig}
                    {...props}
                />
            </div>
        );
    };

export const numbersArrayContainer =
    ({
        id,
        placeholder,
        inputType,
        label,
        disabled,
        decimal,
        validationSchema,
        isConfig
    }: NumberArrayFieldOptions) =>
    props => {
        return (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseNumbersArray
                    placeholder={placeholder}
                    inputtype={"number"}
                    givenid={id}
                    decimal={decimal}
                    disabled={disabled ? "disabled" : ""}
                    isConfig={isConfig}
                    {...props}
                />
            </div>
        );
    };

export const inputAliasContainer =
    ({
        id,
        placeholder,
        inputType,
        label,
        disabled,
        decimal,
        items,
        validationSchema,
        isConfig
    }: InputAliasFieldOptions) =>
    props => {
        return (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseInputAlias
                    placeholder={placeholder}
                    inputtype={"number"}
                    givenid={id}
                    decimal={decimal}
                    disabled={disabled ? "disabled" : ""}
                    items={items}
                    isConfig={isConfig}
                    {...props}
                />
            </div>
        );
    };

export const betJackpotArrayContainer =
    ({
        id,
        placeholder,
        inputType,
        label,
        disabled,
        decimal,
        items,
        validationSchema,
        isConfig
    }: BetJackpotArrayFieldOptions) =>
    props => {
        return (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseBetJackpotArray
                    placeholder={placeholder}
                    inputtype={"number"}
                    givenid={id}
                    decimal={decimal}
                    disabled={disabled ? "disabled" : ""}
                    items={items}
                    isConfig={isConfig}
                    {...props}
                />
            </div>
        );
    };

export const betFreqPrizesArrayContainer =
    ({
         id,
         placeholder,
         inputType,
         label,
         disabled,
         decimal,
         items,
         validationSchema,
         isConfig
     }: BetFreqPrizesArrayFieldOptions) =>
        props => {
            return (
                <div>
                    <div className="label-tooltip">
                        {label && <label className="label">{label}</label>}
                        {renderToolTip(validationSchema)}
                    </div>
                    <BaseBetFreqPrizesArray
                        placeholder={placeholder}
                        inputtype={"number"}
                        givenid={id}
                        decimal={decimal}
                        disabled={disabled ? "disabled" : ""}
                        items={items}
                        isConfig={isConfig}
                        {...props}
                    />
                </div>
            );
        };

export const dateContainer =
    ({
        id,
        placeholder,
        inputType,
        label,
        disabled,
        showtime,
        minutesinterval,
        validationSchema,
        isConfig
    }: DateFieldOptions) =>
    props => {
        return (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseDate
                    placeholder={placeholder}
                    inputtype={inputType}
                    givenid={id}
                    showtime={showtime}
                    minutesinterval={minutesinterval}
                    disabled={disabled ? "disabled" : ""}
                    isConfig={isConfig}
                    {...props}
                />
            </div>
        );
    };

export const selectContainer =
    ({
        id,
        noValueLabel,
        label,
        items,
        sourceChanges,
        multipleSelection,
        validationSchema,
        disabled,
        headerContainer,
        isConfig
    }: SelectFieldOptions) =>
    props => {
        return (
            <div>
                <div className="label-tooltip">
                    {label && (
                        <label className="label" htmlFor={`${id}Select`}>
                            {label}
                        </label>
                    )}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseSelect
                    noValueLabel={noValueLabel}
                    givenid={id}
                    items={items}
                    disabled={disabled}
                    sourceChanges={sourceChanges}
                    multipleSelection={multipleSelection}
                    headerContainer={headerContainer}
                    isConfig={isConfig}
                    {...props}
                />
            </div>
        );
    };

export const switchContainer =
    ({ id, label, disabled, validationSchema }: SwitchFieldOptions) =>
    props =>
        (
            <div>
                <div className="label-tooltip">
                    {label && (
                        <label className="label" htmlFor={`${id}-switch`}>
                            {label}
                        </label>
                    )}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseSwitch givenid={id} {...props} disabled={disabled} />
            </div>
        );

export const checkboxContainer =
    ({ id, text }: CheckboxFieldOptions) =>
    props =>
        (
            <React.Fragment>
                <BaseCheckbox givenid={id} text={text} {...props} />
            </React.Fragment>
        );

export const buttonSelectorContainer =
    ({
        id,
        label,
        items,
        columnClass,
        selectedClass,
        notSelectedClass,
        exclusive,
        validationSchema
    }: ButtonSelectorFieldOptions) =>
    props =>
        (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseButtonSelector
                    givenid={id}
                    items={items}
                    {...props}
                    columnClass={columnClass}
                    exclusive={exclusive}
                    selectedClass={selectedClass}
                    notSelectedClass={notSelectedClass}
                />
            </div>
        );

export const twoPanelSelectorContainer =
    ({
        id,
        label,
        items,
        completeList,
        validationSchema
    }: TwoPanelSelectorOptions) =>
    props =>
        (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseTwoPanelSelector
                    givenid={id}
                    items={items}
                    completeList={completeList}
                    {...props}
                />
            </div>
        );

export const gameSelectorContainer =
    ({
        id,
        label,
        selectedGames,
        exclusive,
        completeList,
        bundlesList,
        disabled,
        validationSchema
    }: GameSelectorFieldOptions) =>
    props =>
        (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseGameSelector
                    givenid={id}
                    selectedGames={selectedGames}
                    bundlesList={bundlesList}
                    exclusive={exclusive}
                    completeList={completeList}
                    disabled={disabled}
                    {...props}
                />
            </div>
        );

export const bulletsSelectorContainer =
    ({
        id,
        label,
        items,
        enableSearch,
        orderByLabel,
        exclusive,
        completeList,
        disabled,
        validationSchema
    }: BulletsSelectOptions) =>
    props =>
        (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <BaseBulletsSelector
                    givenid={id}
                    items={items}
                    enableSearch={enableSearch}
                    orderByLabel={orderByLabel}
                    exclusive={exclusive}
                    completeList={completeList}
                    disabled={disabled}
                    {...props}
                />
            </div>
        );

export const formSetContainer =
    ({
        id,
        subFormTypes,
        formSetLabel,
        initialValues,
        extraFields,
        subsidiary,
        isConfig
    }: FormSetOptions) =>
    props => {
        return (
            <React.Fragment>
                <BaseFormSet
                    givenid={id}
                    formSetLabel={formSetLabel}
                    subFormTypes={subFormTypes}
                    initialValues={initialValues}
                    extraFields={extraFields}
                    isConfig={isConfig}
                    subsidiary={subsidiary}
                    {...props}
                />
            </React.Fragment>
        );
    };

export const formSetWaysContainer =
    ({
        id,
        subFormTypes,
        formSetLabel,
        initialValues,
        presets,
        extraFields,
        isConfig,
        gameInstances
    }: FormSetWaysOptions) =>
    props => {
        return (
            <React.Fragment>
                <BaseFormSetWays
                    givenid={id}
                    formSetLabel={formSetLabel}
                    subFormTypes={subFormTypes}
                    initialValues={initialValues}
                    presets={presets}
                    extraFields={extraFields}
                    isConfig={isConfig}
                    gameInstances={gameInstances}
                    {...props}
                />
            </React.Fragment>
        );
    };
export const groupContainer = (
    {label}) =>
        props => {
            return (
               <BaseGroup label={label} {...props} />
            );
        };


export const inputSearchContainer =
    ({
        id,
        label,
        searchAction,
        items,
        selectedFilters,
        validationSchema
    }: InputSearchFieldOptions) =>
    props =>
        (
            <div>
                <div className="label-tooltip">
                    {label && <label className="label">{label}</label>}
                    {renderToolTip(validationSchema)}
                </div>
                <InputSearch
                    givenid={id}
                    searchAction={searchAction}
                    items={items}
                    selectedFilters={selectedFilters}
                    {...props}
                />
            </div>
        );